import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ScanLine, Sparkles } from 'lucide-react';
import { DialogForm, CurrencyPicker, UserPicker, LtrToken, BidiText } from '../../../shared/components/common';
import { CompanyFormField } from '../../../shared/components/multi-company';
import { useActiveCompany } from '../../../shared/contexts/ActiveCompanyContext';
import { useMyCompanies } from '@/shared/users/hooks/useCompanies';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Label } from '../../../components/ui/label';
import { Button } from '../../../components/ui/button';
import { Switch } from '../../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { InvoiceLineEditor, PendingLineItem } from './InvoiceLineEditor';
import { OrganizationPicker } from '../../master_data/contacts/components/OrganizationPicker';
import { useAccounts } from '../hooks/useAccounts';
import { useTaxRates } from '../hooks/useTaxRates';
import { useTenantLocaleSettings } from '../../../shared/hooks/useTenantSettings';
import { aiApi } from '../services/accountingApi';
import { DuplicateBillWarning } from './DuplicateBillWarning';
import { UnbilledReceiptsPicker } from './UnbilledReceiptsPicker';
import type { BillCreate, AccountSuggestion } from '../types';
import { partitionBillLines } from '../utils/billLineDraft';
import { filterCashAndBankAccounts } from '../utils/cashAndBankAccounts';
import { toDateOnlyString } from '@/shared/utils/dateFormat';

interface BillQuickCreateDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: BillCreate) => void;
  isLoading?: boolean;
  /** P2P-1: build the bill from the vendor's unbilled goods receipts instead of
   *  hand-keying lines. When provided, a receipts picker appears once a vendor
   *  is chosen. Receives the selected receipt ids. */
  onBuildFromReceipts?: (receiptIds: string[]) => void;
  isBuildingFromReceipts?: boolean;
  prefill?: {
    description?: string;
    vendor_name?: string;
    issue_date?: string;
    due_date?: string;
    reference?: string;
    notes?: string;
    is_paid_immediately?: boolean;
    currency_code?: string;
    lines?: PendingLineItem[];
  };
}

export function BillQuickCreateDialog({ open, onClose, onSubmit, isLoading, onBuildFromReceipts, isBuildingFromReceipts, prefill }: BillQuickCreateDialogProps) {
  const { t } = useTranslation('accounting');
  const navigate = useNavigate();
  const { data: accountsData } = useAccounts({ is_active: true, limit: 500 });
  const { data: taxRatesData } = useTaxRates({ is_active: true, limit: 100 });
  const { data: localeSettings } = useTenantLocaleSettings();
  const defaultCurrency = localeSettings?.default_currency || 'USD';

  const { activeCompanyId } = useActiveCompany();
  const { data: myCompaniesData } = useMyCompanies();
  const hasMultipleCompanies = (myCompaniesData?.data?.length ?? 0) > 1;
  const [companyId, setCompanyId] = useState('');
  const [vendorId, setVendorId] = useState('');
  const [vendorName, setVendorName] = useState('');
  const [employeeId, setEmployeeId] = useState<string | null>(null);
  const [description, setDescription] = useState('');
  const [issueDate, setIssueDate] = useState(toDateOnlyString(new Date()));
  const [dueDate, setDueDate] = useState('');
  const [isPaidImmediately, setIsPaidImmediately] = useState(false);
  const [paidFromAccountId, setPaidFromAccountId] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [currencyCode, setCurrencyCode] = useState(defaultCurrency);
  // §16.1: no always-present empty add-row — start with zero lines and let the
  // editor's AddLineButton create the first one (handleSubmit blocks an empty
  // bill with a visible lineRequired error, never a silent drop).
  const [lines, setLines] = useState<PendingLineItem[]>([]);
  const [aiSuggestion, setAiSuggestion] = useState<(AccountSuggestion & { organizationId: string }) | null>(null);
  // Line-item validation: a client-side guard that blocks submit (never a silent
  // drop) when an entered line has no expense account, and reveals the per-line
  // account column so the user can pick one.
  const [lineError, setLineError] = useState<string | null>(null);
  const [showLineAccounts, setShowLineAccounts] = useState(false);

  const handleImportFromDocument = useCallback(() => {
    onClose();
    navigate('/accounting/import-bills/new');
  }, [onClose, navigate]);

  const billTotal = useMemo(
    () =>
      lines.reduce(
        (sum, l) =>
          sum + (l.line_total || (l.quantity || 1) * (l.unit_price || 0)),
        0,
      ),
    [lines],
  );

  useEffect(() => {
    if (open) {
      setCompanyId(activeCompanyId ?? '');
      setVendorId('');
      setVendorName(prefill?.vendor_name || '');
      setEmployeeId(null);
      setAiSuggestion(null);
      setDescription(prefill?.description || '');
      const today = toDateOnlyString(new Date());
      setIssueDate(prefill?.issue_date || today);
      const due = new Date();
      due.setDate(due.getDate() + 30);
      setDueDate(prefill?.due_date || toDateOnlyString(due));
      setIsPaidImmediately(prefill?.is_paid_immediately ?? false);
      setCurrencyCode(prefill?.currency_code || defaultCurrency);
      setPaidFromAccountId('');
      setPaymentMethod('');
      setReference(prefill?.reference || '');
      setNotes(prefill?.notes || '');
      setLines(prefill?.lines?.length ? prefill.lines : []);
      setLineError(null);
      setShowLineAccounts(false);
    }
  }, [open, prefill, activeCompanyId, defaultCurrency]);

  const accounts = useMemo(() => accountsData?.data || [], [accountsData]);
  const taxRates = taxRatesData?.data || [];

  // Paid-from must be a real cash/bank account — the whole asset chart (AR,
  // Inventory, Accumulated Depreciation…) would post balanced-but-wrong JEs.
  const bankAccounts = useMemo(() => filterCashAndBankAccounts(accounts), [accounts]);

  const handleSubmit = () => {
    // Never silently drop an entered line: apply the AI-suggested account as a
    // fallback, then block the submit (revealing the account column + a visible
    // error) if any entered line still lacks an expense account or description.
    const { entered, hasIncomplete } = partitionBillLines(lines, aiSuggestion?.account_id ?? undefined);
    if (entered.length === 0) {
      setLineError(t('bills.lineRequired'));
      return;
    }
    if (hasIncomplete) {
      setShowLineAccounts(true);
      setLineError(t('bills.lineAccountRequired'));
      return;
    }
    setLineError(null);
    if (isPaidImmediately && !paidFromAccountId) return;
    // Multi-company guard: require explicit pick when sticky picker = "All".
    if (!companyId && activeCompanyId === null && hasMultipleCompanies) return;

    onSubmit({
      company_id: companyId || undefined,
      lines: entered.map((l) => ({
        description: l.description,
        account_id: l.account_id,
        offering_id: l.offering_id || undefined,
        quantity: l.quantity,
        unit_price: l.unit_price,
        discount_percent: l.discount_percent,
        tax_rate_id: l.tax_rate_id || undefined,
        tax_amount: l.tax_amount,
      })),
      is_paid_immediately: isPaidImmediately,
      currency_code: currencyCode,
      issue_date: issueDate,
      due_date: isPaidImmediately ? undefined : dueDate || undefined,
      vendor_id: vendorId || undefined,
      vendor_name: vendorName || undefined,
      employee_id: employeeId || undefined,
      description: description || undefined,
      reference: reference || undefined,
      notes: notes || undefined,
      paid_from_account_id: isPaidImmediately ? paidFromAccountId : undefined,
      payment_method: isPaidImmediately ? paymentMethod || undefined : undefined,
    });
  };

  return (
    <DialogForm
      open={open}
      onClose={onClose}
      onSubmit={handleSubmit}
      title={t('bills.createBill')}
      submitLabel={t('bills.createBill')}
      isLoading={isLoading}
      maxWidth="lg"
    >
      <div className="space-y-4">
        {/* Import-from-document affordance: AI parsing now lives in the shared
            review-before-finalize import flow, not an inline dialog prefill. */}
        {!prefill?.lines?.length && (
          <button
            type="button"
            onClick={handleImportFromDocument}
            className="flex w-full items-center gap-2 rounded-lg border border-dashed border-muted-foreground/25 p-3 text-left text-sm text-muted-foreground transition-colors hover:border-primary/50 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <ScanLine className="h-4 w-4 shrink-0" />
            <span className="flex-1">{t('bills.importFromDocument')}</span>
          </button>
        )}

        {/* Paid immediately toggle */}
        <div className="flex items-center gap-3 p-3 rounded-lg border">
          <Switch
            checked={isPaidImmediately}
            onCheckedChange={setIsPaidImmediately}
          />
          <div>
            <Label className="font-medium">{t('bills.paidImmediately')}</Label>
            <p className="text-xs text-muted-foreground">
              {isPaidImmediately ? t('bills.paidImmediatelyHint') : t('bills.payLaterHint')}
            </p>
          </div>
        </div>

        <CompanyFormField value={companyId} onChange={setCompanyId} />

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label>{t('bills.vendor')}</Label>
            <OrganizationPicker
              value={vendorId}
              onChange={setVendorId}
              onCompanySelected={(company) => {
                setVendorName(company?.name ?? '');
                setAiSuggestion(null);
                // Auto-fill currency from vendor's default (user can override)
                if (company?.default_currency_code) {
                  setCurrencyCode(company.default_currency_code);
                } else {
                  setCurrencyCode(defaultCurrency);
                }
                if (company) {
                  aiApi.suggestAccount(company.name, 'expense', company.industry ?? undefined).then((res) => {
                    const suggestion = res.data;
                    if (suggestion.account_id) {
                      setAiSuggestion({ ...suggestion, organizationId: company.id });
                      setLines((prev) =>
                        prev.map((line) =>
                          !line.account_id ? { ...line, account_id: suggestion.account_id! } : line
                        )
                      );
                    }
                  }).catch(() => { /* Silently fail — user can pick manually */ });
                }
              }}
              organizationRelationshipFilter="vendor"
              placeholder={t('bills.vendorPlaceholder')}
              addNewLabel={t('contacts:companyPicker.addNewVendor')}
              defaultRelationships={['vendor']}
              allowClear
            />
          </div>
          <div className="space-y-2">
            <Label>{t('bills.date')}</Label>
            <Input
              type="date"
              value={issueDate}
              onChange={(e) => setIssueDate(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>{t('bills.currency')}</Label>
            <CurrencyPicker
              value={currencyCode}
              onChange={(code) => setCurrencyCode(code || defaultCurrency)}
              compact
              size="sm"
            />
          </div>
        </div>

        {onBuildFromReceipts && vendorId && (
          <UnbilledReceiptsPicker
            vendorId={vendorId}
            companyId={companyId || undefined}
            onBuild={onBuildFromReceipts}
            isBuilding={isBuildingFromReceipts}
          />
        )}

        {aiSuggestion && (
          <div className="flex items-center gap-2 p-2 bg-primary/5 rounded text-sm">
            <Sparkles className="h-3.5 w-3.5 text-primary shrink-0" />
            <span className="flex-1">{t('bills.aiSuggestedAccount', { account: aiSuggestion.account_name })}</span>
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setAiSuggestion(null)}>
              {t('common:app.dismiss')}
            </Button>
          </div>
        )}

        {vendorId && billTotal > 0 && (
          <DuplicateBillWarning
            vendorId={vendorId}
            vendorInvoiceNumber={reference || undefined}
            total={billTotal}
            issueDate={issueDate}
            currencyCode={currencyCode}
          />
        )}

        <div className="space-y-2">
          <Label>{t('bills.employee')}</Label>
          <UserPicker
            value={employeeId}
            onChange={setEmployeeId}
            placeholder={t('bills.employeePlaceholder')}
            filterUserTypes={['internal']}
            size="sm"
          />
        </div>

        {!isPaidImmediately && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t('bills.dueDate')}</Label>
              <Input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('bills.reference')}</Label>
              <Input
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder={t('bills.referencePlaceholder')}
              />
            </div>
          </div>
        )}

        {isPaidImmediately && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t('bills.paidFrom')}</Label>
              <Select value={paidFromAccountId} onValueChange={setPaidFromAccountId}>
                <SelectTrigger>
                  <SelectValue placeholder={t('bills.selectPaidFrom')} />
                </SelectTrigger>
                <SelectContent>
                  {bankAccounts.map((acc) => (
                    <SelectItem key={acc.id} value={acc.id}>
                      <LtrToken>{acc.account_number}</LtrToken> - <BidiText>{acc.name}</BidiText>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t('bills.paymentMethod')}</Label>
              <Input
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                placeholder={t('bills.paymentMethodPlaceholder')}
              />
            </div>
          </div>
        )}

        {isPaidImmediately && (
          <div className="space-y-2">
            <Label>{t('bills.reference')}</Label>
            <Input
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder={t('bills.referencePlaceholder')}
            />
          </div>
        )}

        <div className="space-y-2">
          <Label>{t('lineItems.title')}</Label>
          <InvoiceLineEditor
            lines={lines}
            onChange={(next) => {
              setLines(next);
              if (lineError) setLineError(null);
            }}
            accounts={accounts}
            taxRates={taxRates}
            accountTypeFilter={['expense']}
            defaultAccountId={aiSuggestion?.account_id ?? undefined}
            forceAdvanced={showLineAccounts}
          />
          {lineError && (
            <p className="text-sm text-destructive">{lineError}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label>{t('bills.notesLabel')}</Label>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
          />
        </div>
      </div>
    </DialogForm>
  );
}
